import { useState, useId } from "react";
import { Marker, Source, Layer } from "react-map-gl/mapbox";
import { DataSourceInfo } from "@/endpoints/rain/rain";
import { MapBase } from "@/components/MapBase";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { CloudRain } from "lucide-react";

const fitBounds = (
	lat1: number,
	lon1: number,
	lat2: number,
	lon2: number,
): { latitude: number; longitude: number; zoom: number } => {
	const latitude = (lat1 + lat2) / 2;
	const longitude = (lon1 + lon2) / 2;
	const latDiff = Math.abs(lat1 - lat2);
	const lonDiff = Math.abs(lon1 - lon2);
	const maxDiff = Math.max(latDiff, lonDiff);
	const zoom = maxDiff > 0 ? Math.min(11, Math.max(6, -Math.log2(maxDiff) + 5.5)) : 11;
	return { latitude, longitude, zoom };
};

const GaugeConnectionMap = ({
	structureLat,
	structureLon,
	stationLat,
	stationLon,
	stationName,
	distanceKm,
	provider,
}: {
	structureLat: number;
	structureLon: number;
	stationLat: number;
	stationLon: number;
	stationName: string;
	distanceKm: number;
	provider: string;
	structureName: string;
}) => {
	const [mapLoaded, setMapLoaded] = useState(false);
	const id = useId();
	const sourceId = `gauge-line-${id}`;
	const layerId = `gauge-line-layer-${id}`;
	const view = fitBounds(structureLat, structureLon, stationLat, stationLon);

	const lineGeoJson: GeoJSON.Feature = {
		type: "Feature",
		properties: {},
		geometry: {
			type: "LineString",
			coordinates: [
				[structureLon, structureLat],
				[stationLon, stationLat],
			],
		},
	};

	return (
		<div>
			<div className="w-full h-44 overflow-hidden">
				<MapBase initialViewState={view} isStatic onLoad={() => setMapLoaded(true)}>
					{mapLoaded && (
						<Source id={sourceId} type="geojson" data={lineGeoJson}>
							<Layer
								id={layerId}
								type="line"
								paint={{
									"line-color": "#06b6d4",
									"line-width": 2,
									"line-dasharray": [4, 3],
								}}
							/>
						</Source>
					)}
					<Marker latitude={structureLat} longitude={structureLon} anchor="center">
						<div className="w-3 h-3 rounded-full bg-primary border-2 border-white shadow" />
					</Marker>
					<Marker latitude={stationLat} longitude={stationLon} anchor="center">
						<div className="flex items-center justify-center w-6 h-6 rounded-full bg-cyan-500 border-2 border-white shadow">
							<CloudRain className="w-3.5 h-3.5 text-white" />
						</div>
					</Marker>
				</MapBase>
			</div>
			<div className="px-2 py-1 text-center">
				<p className="text-xs font-medium text-foreground leading-tight">{stationName}</p>
				<p className="text-[11px] text-muted-foreground leading-tight">
					Rain data from {provider} · {distanceKm.toFixed(1)} km away
				</p>
			</div>
		</div>
	);
};

export const SourceInfoBadge = ({
	sourceInfo,
	structureLat,
	structureLon,
	structureName,
	compact = false,
}: {
	sourceInfo: DataSourceInfo;
	structureLat: number;
	structureLon: number;
	structureName: string;
	compact?: boolean;
}) => {
	const isGauge = sourceInfo.source === "rain_gauge";
	const station = sourceInfo.station;

	const chip = (
		<span className="px-1.5 py-px text-[9px] font-medium uppercase tracking-wide rounded bg-cyan-50 text-cyan-700 border border-cyan-200 dark:bg-cyan-950 dark:text-cyan-300 dark:border-cyan-800">
			{isGauge ? "Rain Gauge" : "Radar"}
		</span>
	);

	const badge = (
		<div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
			{chip}
			<span>
				{isGauge && station
					? `Rain data from ${station.provider} · ${station.distanceKm.toFixed(1)} km away`
					: "Precipitation data from radar composite"}
			</span>
		</div>
	);

	if (compact) {
		const tooltipContent = (
			<>
				{isGauge && station ? (
					<GaugeConnectionMap
						structureLat={structureLat}
						structureLon={structureLon}
						stationLat={station.lat}
						stationLon={station.lon}
						stationName={station.name}
						distanceKm={station.distanceKm}
						provider={station.provider}
						structureName={structureName}
					/>
				) : (
					<p className="px-3 py-2 text-xs">Precipitation data from radar composite</p>
				)}
			</>
		);

		return (
			<Tooltip delayDuration={300}>
				<TooltipTrigger asChild>
					<span className="inline-flex items-center cursor-default">{chip}</span>
				</TooltipTrigger>
				<TooltipContent
					side="bottom"
					align="end"
					sideOffset={8}
					className="w-72 p-0 bg-popover text-popover-foreground border border-border shadow-md overflow-hidden"
				>
					{tooltipContent}
				</TooltipContent>
			</Tooltip>
		);
	}

	if (!isGauge || !station) return badge;

	return (
		<Tooltip delayDuration={300}>
			<TooltipTrigger asChild>
				<span className="cursor-default">{badge}</span>
			</TooltipTrigger>
			<TooltipContent
				side="bottom"
				align="start"
				sideOffset={8}
				className="w-72 p-0 bg-popover text-popover-foreground border border-border shadow-md overflow-hidden"
			>
				<GaugeConnectionMap
					structureLat={structureLat}
					structureLon={structureLon}
					stationLat={station.lat}
					stationLon={station.lon}
					stationName={station.name}
					distanceKm={station.distanceKm}
					provider={station.provider}
					structureName={structureName}
				/>
			</TooltipContent>
		</Tooltip>
	);
};
